import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, FileText, Paperclip, Plus, Save, Trash2, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSaveSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import type { DeliveryChallanDocument, DeliveryChallanLine } from '../types'
import { newDeliveryChallanLine } from '../types'
import { deliveryChallanService, type DcCustomerOption, type DcOrderOption } from '@/services/deliveryChallanService'
import { useAuthStore } from '@/store/auth.store'
import { MOCK_BATCHES_BY_ITEM, MOCK_ITEMS, computeDocumentTotal, defaultNewDocument } from '../mockStore'
import { config } from '@/core/config'

/** Resolve a server-relative attachment path to an absolute URL the browser can open. */
function resolveAttachmentUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

type AttachmentFieldProps = {
  inputId: string
  mode: 'new' | 'edit'
  file: File | null
  existingUrl: string
  existingName: string
  onFileChange: (file: File | null) => void
}

/**
 * Single polished attachment control matching the sales-order/dispatch design:
 *  - "new" mode: clean drop-zone style button to pick a file.
 *  - "edit" mode: shows the saved/picked file with a clickable filename,
 *    eye preview, Replace and Clear actions. The underlying file input is
 *    hidden so the chrome is consistent across browsers.
 */
const AttachmentField: React.FC<AttachmentFieldProps> = ({
  inputId,
  mode,
  file,
  existingUrl,
  existingName,
  onFileChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hasExisting = Boolean(existingUrl)
  const hasAnyAttachment = hasExisting || Boolean(file)
  const openPicker = () => fileInputRef.current?.click()
  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    onFileChange(null)
  }
  const fileName = file?.name || existingName || 'No file chosen'
  const showCard = mode === 'edit' && hasAnyAttachment

  return (
    <div className="space-y-1">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        aria-label="File"
      />

      {showCard ? (
        <div className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20">
          <Paperclip className="h-4 w-4 shrink-0 text-primary" />
          {hasExisting && !file ? (
            <a
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary hover:underline"
              title={fileName}
            >
              {fileName}
            </a>
          ) : (
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{fileName}</span>
          )}
          <div className="flex shrink-0 items-center gap-1">
            {hasExisting && !file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => window.open(existingUrl, '_blank', 'noopener,noreferrer')}
                title="View"
              >
                <Eye className="h-4 w-4" />
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2" onClick={openPicker}>
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Replace</span>
            </Button>
            {file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                onClick={clearFile}
                title="Clear"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 text-left transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20"
        >
          <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm">
            {file ? (
              <span className="text-foreground">{file.name}</span>
            ) : (
              <span className="text-muted-foreground">Choose a file to upload</span>
            )}
          </span>
          {file ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                clearFile()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  clearFile()
                }
              }}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive"
              title="Clear"
            >
              <X className="h-4 w-4" />
            </span>
          ) : null}
        </button>
      )}

      {mode === 'edit' && file ? (
        <p className="truncate text-xs text-emerald-600 dark:text-emerald-400">
          New file selected — will replace on save
        </p>
      ) : null}
    </div>
  )
}

export const InventoryDeliveryChallanEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const { id: idFromEditPath } = useParams<{ id: string }>()
  const location = useLocation()
  /** `/new` is a static route segment — there is no `:id` param on that URL */
  const routeId =
    idFromEditPath ?? (location.pathname.endsWith('/delivery-challan/new') ? 'new' : undefined)
  const navigate = useNavigate()
  const isEdit = Boolean(routeId && routeId !== 'new')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [doc, setDoc] = useState<DeliveryChallanDocument | null>(null)
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [customers, setCustomers] = useState<DcCustomerOption[]>([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [orders, setOrders] = useState<DcOrderOption[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [orderLineItemExtras, setOrderLineItemExtras] = useState<Array<{ id: number; name: string }>>([])
  const [orderLineBatchExtras, setOrderLineBatchExtras] = useState<
    Record<string, Array<{ id: number; name: string; stock: number }>>
  >({})
  const [orderLinesLoading, setOrderLinesLoading] = useState(false)
  const orderLinesRequestRef = useRef(0)

  const customerIdForOrders = useMemo(() => {
    const n = Number(doc?.customerId)
    return Number.isFinite(n) && n > 0 ? n : 0
  }, [doc?.customerId])

  useEffect(() => {
    if (!branchId) {
      setCustomers([])
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        setCustomersLoading(true)
        const list = await deliveryChallanService.getCustomers(branchId)
        if (!cancelled) setCustomers(list)
      } catch (error: unknown) {
        if (!cancelled) {
          setCustomers([])
          const msg =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            t('deliveryChallan.failedLoadCustomers')
          toast.error(msg)
        }
      } finally {
        if (!cancelled) setCustomersLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [branchId, t])

  useEffect(() => {
    if (!branchId || !customerIdForOrders) {
      setOrders([])
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        setOrdersLoading(true)
        const list = await deliveryChallanService.getOrders(branchId, customerIdForOrders)
        if (!cancelled) setOrders(list)
      } catch (error: unknown) {
        if (!cancelled) {
          setOrders([])
          const msg =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            t('deliveryChallan.failedLoadOrders')
          toast.error(msg)
        }
      } finally {
        if (!cancelled) setOrdersLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [branchId, customerIdForOrders, t])

  useEffect(() => {
    if (!routeId) return
    if (routeId === 'new') {
      setAttachmentFile(null)
      setOrderLineItemExtras([])
      setOrderLineBatchExtras({})
      setDoc(defaultNewDocument())
      return
    }
    const oid = Number(routeId)
    if (!oid) {
      toast.error('Invalid challan.')
      navigate('/inventory/delivery-challan')
      return
    }
    if (!branchId) {
      setDoc(null)
      return
    }
    let cancelled = false
    setDoc(null)
    setAttachmentFile(null)
    setOrderLineItemExtras([])
    setOrderLineBatchExtras({})
    void (async () => {
      try {
        const loaded = await deliveryChallanService.loadChallanForEdit(oid, branchId)
        if (cancelled) return
        setDoc(loaded)
      } catch (error: unknown) {
        if (cancelled) return
        const msg =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (error instanceof Error ? error.message : null) ??
          'Failed to load delivery challan.'
        toast.error(msg)
        navigate('/inventory/delivery-challan')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [routeId, branchId, navigate])

  useEffect(() => {
    if (routeId === 'new' || !doc?.id || !branchId) return
    if (!doc.lines?.some((l) => Number(l.itemId) > 0 && Number(l.batchId) > 0)) return
    let cancelled = false
    void deliveryChallanService.enrichLineLookupsFromLines(doc.lines, branchId).then((ex) => {
      if (!cancelled) {
        setOrderLineItemExtras(ex.itemExtras)
        setOrderLineBatchExtras(ex.batchExtrasByItem)
      }
    })
    return () => {
      cancelled = true
    }
  }, [routeId, doc?.id, branchId]) // eslint-disable-line react-hooks/exhaustive-deps -- omit doc.lines: enrich on open only, not on every line edit

  const lines = doc?.lines ?? []

  const mergedLineItems = useMemo(() => {
    const m = new Map<number, { id: number; name: string }>()
    MOCK_ITEMS.forEach((i) => m.set(i.id, i))
    orderLineItemExtras.forEach((i) => {
      if (!m.has(i.id)) m.set(i.id, i)
    })
    return [...m.values()].sort((a, b) => a.id - b.id)
  }, [orderLineItemExtras])

  const getBatchesForItemId = useCallback(
    (itemId: string) => {
      const base = MOCK_BATCHES_BY_ITEM[itemId] || []
      const extras = orderLineBatchExtras[itemId] || []
      const m = new Map<number, { id: number; name: string; stock: number }>()
      for (const b of base) m.set(b.id, b)
      for (const b of extras) m.set(b.id, b)
      return [...m.values()]
    },
    [orderLineBatchExtras],
  )

  const orderSelectOptions = useMemo((): DcOrderOption[] => {
    if (!doc?.orderId) return orders
    if (orders.some((o) => String(o.id) === doc.orderId)) return orders
    const idNum = Number(doc.orderId)
    if (!Number.isFinite(idNum) || idNum <= 0) return orders
    return [
      {
        id: idNum,
        label: doc.orderCode || String(doc.orderId),
        code: doc.orderCode || '',
        reference: doc.orderReference || '',
        warehouseName: doc.orderWarehouseName || '',
        status: doc.orderStatus || '',
      },
      ...orders,
    ]
  }, [doc, orders])

  const setHeader = (patch: Partial<DeliveryChallanDocument>) => {
    setDoc((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  const updateLine = (index: number, patch: Partial<DeliveryChallanLine>) => {
    setDoc((prev) => {
      if (!prev) return prev
      const next = [...prev.lines]
      const row = next[index]
      if (!row) return prev
      const merged: DeliveryChallanLine = { ...row, ...patch }
      if (patch.itemId !== undefined && patch.itemId !== row.itemId) {
        merged.batchId = ''
        merged.availableQuantity = 0
        merged.quantityReceived = 0
        merged.quantityReturn = 0
      }
      if (patch.batchId !== undefined && patch.batchId !== row.batchId) {
        merged.availableQuantity = 0
        merged.quantityReceived = 0
        merged.quantityReturn = 0
      }
      const batches = getBatchesForItemId(merged.itemId)
      const batch = batches.find((b) => String(b.id) === merged.batchId)
      if (!merged.fromOrder && batch) merged.availableQuantity = batch.stock
      const avail = Number(merged.availableQuantity || 0)
      let qr = Math.max(0, Number(merged.quantityReceived || 0))
      const qret = Math.max(0, Number(merged.quantityReturn || 0))
      if (avail > 0) qr = Math.min(qr, avail)
      merged.quantityReceived = qr
      merged.quantityReturn = qret
      next[index] = merged
      return { ...prev, lines: next }
    })
  }

  const addLines = (n: number) => {
    setDoc((prev) =>
      prev ? { ...prev, lines: [...prev.lines, ...Array.from({ length: n }, () => newDeliveryChallanLine())] } : prev,
    )
  }

  const handleLineDelete = (index: number) => {
    setDoc((prev) => {
      if (!prev) return prev
      if (prev.lines.length <= 1) return prev
      return { ...prev, lines: prev.lines.filter((_, i) => i !== index) }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!doc) return
    if (!doc.orderId) return toast.error(t('deliveryChallan.validationOrder'))
    if (!doc.customerId) return toast.error(t('deliveryChallan.validationCustomer'))

    const customerName =
      customers.find((c) => String(c.id) === doc.customerId)?.name?.trim() ?? doc.customerName

    const lineHasQty = (l: DeliveryChallanLine) =>
      Number(l.quantityReceived || 0) > 0 || Number(l.quantityReturn || 0) > 0
    const activeLines = lines.filter(
      (l) =>
        !l.isBonus &&
        Number(l.itemId || 0) > 0 &&
        Number(l.batchId || 0) > 0 &&
        lineHasQty(l),
    )
    if (activeLines.length === 0) return toast.error(t('deliveryChallan.validationLines'))

    const nextDoc: DeliveryChallanDocument = {
      ...doc,
      customerName,
      attachmentFileName: attachmentFile?.name?.trim() || doc.attachmentFileName,
      lines: lines.filter(
        (l) =>
          !l.isBonus &&
          Number(l.itemId || 0) > 0 &&
          Number(l.batchId || 0) > 0 &&
          lineHasQty(l),
      ),
    }

    if (nextDoc.lines.length === 0) return toast.error(t('deliveryChallan.validationLines'))
    if (!branchId) return toast.error(t('deliveryChallan.selectBranchFirst'))

    const customerNumericId = Number(doc.customerId)
    const orderNumericId = Number(doc.orderId)
    if (!Number.isFinite(customerNumericId) || customerNumericId <= 0) {
      return toast.error(t('deliveryChallan.validationCustomer'))
    }
    if (!Number.isFinite(orderNumericId) || orderNumericId <= 0) {
      return toast.error(t('deliveryChallan.validationOrder'))
    }

    const savePayload = {
      id: isEdit && doc.id > 0 ? doc.id : 0,
      customerId: customerNumericId,
      orderId: orderNumericId,
      date: doc.challanDate,
      supplyDate: doc.expectedDeliveryDate,
      branchId,
      attachmentFile,
      lines: nextDoc.lines,
    }

    try {
      setIsSaving(true)
      if (isEdit && doc.id > 0) {
        await deliveryChallanService.update(savePayload)
      } else {
        await deliveryChallanService.save({ ...savePayload, id: 0 })
      }
      await showSaveSuccess(isEdit, t(isEdit ? 'deliveryChallan.updated' : 'deliveryChallan.saved'))
      navigate('/inventory/delivery-challan')
    } catch (error: unknown) {
      const msg =
        error instanceof Error && error.message
          ? error.message
          : (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            t('deliveryChallan.failedSave')
      toast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  if (!doc) {
    if (isEdit && !branchId) {
      return (
        <div className="p-6 text-sm text-muted-foreground">
          {t('deliveryChallan.selectBranchFirst')}
        </div>
      )
    }
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('deliveryChallan.loading')}
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('navigation.inventoryDeliveryChallan')}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isEdit && doc.code ? `${t('deliveryChallan.codeLabel')}: ${doc.code}` : t('deliveryChallan.formSubtitle')}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('deliveryChallan.sectionDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>{t('deliveryChallan.challanDate')}</Label>
              <Input
                type="date"
                value={doc.challanDate}
                onChange={(e) => setHeader({ challanDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('deliveryChallan.customer')}</Label>
              <Select
                value={doc.customerId || undefined}
                onValueChange={(v) => {
                  const name = customers.find((c) => String(c.id) === v)?.name ?? ''
                  setOrderLineItemExtras([])
                  setOrderLineBatchExtras({})
                  setHeader({
                    customerId: v,
                    customerName: name,
                    orderId: '',
                    orderCode: '',
                    orderReference: '',
                    orderWarehouseName: '',
                    orderStatus: '',
                  })
                }}
                disabled={!branchId || customersLoading || customers.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !branchId
                        ? t('deliveryChallan.selectBranchFirst')
                        : customersLoading
                          ? t('deliveryChallan.loadingCustomers')
                          : t('deliveryChallan.selectCustomer')
                    }
                  />
                </SelectTrigger>
                <SelectContent searchable>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('deliveryChallan.salesOrder')}</Label>
              <Select
                value={doc.orderId || undefined}
                onValueChange={(v) => {
                  const ord = orderSelectOptions.find((o) => String(o.id) === v)
                  const orderNumericId = Number(v)
                  setDoc((prev) => {
                    if (!prev) return prev
                    return {
                      ...prev,
                      orderId: v,
                      orderCode: ord?.code ?? ord?.label ?? '',
                      orderReference: ord?.reference ?? '',
                      orderWarehouseName: ord?.warehouseName ?? '',
                      orderStatus: ord?.status ?? '',
                    }
                  })
                  if (!branchId || !Number.isFinite(orderNumericId) || orderNumericId <= 0) return

                  const req = ++orderLinesRequestRef.current
                  void (async () => {
                    try {
                      setOrderLinesLoading(true)
                      const hydrated = await deliveryChallanService.hydrateOrderLinesForChallan(
                        orderNumericId,
                        branchId,
                      )
                      if (req !== orderLinesRequestRef.current) return

                      setOrderLineItemExtras(hydrated.itemExtras)
                      setOrderLineBatchExtras(hydrated.batchExtrasByItem)

                      setDoc((prev) => {
                        if (!prev || String(prev.orderId) !== String(v)) return prev
                        if (hydrated.lines.length === 0) {
                          toast.error(t('deliveryChallan.noOrderLines'))
                          const emptyLines = [newDeliveryChallanLine()]
                          return { ...prev, lines: emptyLines, total: computeDocumentTotal(emptyLines) }
                        }
                        return { ...prev, lines: hydrated.lines, total: computeDocumentTotal(hydrated.lines) }
                      })
                    } catch (error: unknown) {
                      if (req !== orderLinesRequestRef.current) return
                      const msg =
                        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                        t('deliveryChallan.failedLoadOrderLines')
                      toast.error(msg)
                    } finally {
                      if (req === orderLinesRequestRef.current) setOrderLinesLoading(false)
                    }
                  })()
                }}
                disabled={
                  !branchId ||
                  !customerIdForOrders ||
                  ordersLoading ||
                  orderLinesLoading ||
                  orderSelectOptions.length === 0
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !doc.customerId
                        ? t('deliveryChallan.selectCustomerBeforeOrder')
                        : ordersLoading
                          ? t('deliveryChallan.loadingOrders')
                          : orderLinesLoading
                            ? t('deliveryChallan.loadingOrderLines')
                            : orderSelectOptions.length === 0
                              ? t('deliveryChallan.noOrdersForCustomer')
                              : t('deliveryChallan.selectOrder')
                    }
                  />
                </SelectTrigger>
                <SelectContent searchable>
                  {orderSelectOptions.map((o) => (
                    <SelectItem key={o.id} value={String(o.id)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('deliveryChallan.expectedDelivery')}</Label>
              <Input
                type="date"
                value={doc.expectedDeliveryDate}
                onChange={(e) => setHeader({ expectedDeliveryDate: e.target.value })}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="delivery-challan-attachment">{t('deliveryChallan.file')}</Label>
              {(() => {
                const existingUrl = resolveAttachmentUrl(doc.attachmentFilePath)
                const existingName = doc.attachmentFileName ||
                  (doc.attachmentFilePath
                    ? doc.attachmentFilePath.split(/[\\/]/).pop() || 'attachment'
                    : '')
                return (
                  <AttachmentField
                    inputId="delivery-challan-attachment"
                    mode={isEdit ? 'edit' : 'new'}
                    file={attachmentFile}
                    existingUrl={isEdit ? existingUrl : ''}
                    existingName={isEdit ? existingName : ''}
                    onFileChange={setAttachmentFile}
                  />
                )
              })()}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('deliveryChallan.sectionLines')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
                <Plus className="mr-1 h-4 w-4" />
                {t('deliveryChallan.addRow')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
                {t('deliveryChallan.add3Rows')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
                {t('deliveryChallan.add5Rows')}
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {[
                      t('deliveryChallan.colItem'),
                      t('deliveryChallan.colBatch'),
                      t('deliveryChallan.colAvailableQty'),
                      t('deliveryChallan.colQtyReceived'),
                      t('deliveryChallan.colQtyReturn'),
                      '',
                    ].map((h, hi) => (
                      <TableHead key={`dc-col-${hi}`} className={tableHeaderClass}>
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, idx) => {
                    const itemReadOnlyLabel =
                      l.itemDisplayName?.trim() ||
                      mergedLineItems.find((it) => String(it.id) === l.itemId)?.name?.trim() ||
                      (l.itemId ? `Item ${l.itemId}` : '')
                    const itemLabel = l.isBonus
                      ? `${t('deliveryChallan.bonus')}: ${itemReadOnlyLabel}`
                      : itemReadOnlyLabel
                    return (
                    <TableRow key={`${doc.id}-${idx}`} className={l.isBonus ? 'bg-muted/40' : undefined}>
                      <TableCell className="py-2">
                        <Input
                          readOnly
                          className="h-9 bg-muted/50"
                          value={itemLabel}
                          placeholder={t('deliveryChallan.selectItem')}
                          title={itemLabel || undefined}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        {l.fromOrder ? (
                          <Input
                            readOnly
                            className="h-9 bg-muted/50"
                            value={l.batchDisplayName?.trim() || (l.batchId ? `Batch ${l.batchId}` : '')}
                            title={l.batchDisplayName?.trim() || undefined}
                          />
                        ) : (
                          <Select
                            value={l.batchId || undefined}
                            onValueChange={(v) => updateLine(idx, { batchId: v })}
                            disabled={!l.itemId}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={t('deliveryChallan.selectBatch')} />
                            </SelectTrigger>
                            <SelectContent searchable>
                              {getBatchesForItemId(l.itemId).map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>
                                  {b.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 bg-muted/50 text-center" value={l.availableQuantity || 0} />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          readOnly={isEdit || Boolean(l.isBonus)}
                          className={
                            isEdit || l.isBonus
                              ? 'h-9 cursor-default bg-muted/50 text-center'
                              : 'h-9 text-center'
                          }
                          value={l.quantityReceived ?? ''}
                          onChange={
                            isEdit || l.isBonus
                              ? undefined
                              : (e) =>
                                  updateLine(idx, { quantityReceived: Number(e.target.value) || 0 })
                          }
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          value={l.quantityReturn ?? ''}
                          readOnly={Boolean(l.isBonus)}
                          className={l.isBonus ? 'h-9 cursor-default bg-muted/50 text-center' : 'h-9 text-center'}
                          onChange={
                            l.isBonus
                              ? undefined
                              : (e) =>
                                  updateLine(idx, { quantityReturn: Number(e.target.value) || 0 })
                          }
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          title={t('lookups.deleteRow')}
                          disabled={Boolean(l.isBonus)}
                          onClick={() => handleLineDelete(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/inventory/delivery-challan')}>
            {t('deliveryChallan.back')}
          </Button>
          <Button type="submit" disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? t('deliveryChallan.saving') : isEdit ? t('deliveryChallan.update') : t('deliveryChallan.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}
